import { Box, Typography, Tooltip, Stack, debounce } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import ReactECharts from 'echarts-for-react';
import { ECElementEvent } from 'echarts';
import { useEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ProgressChartWidgetProps } from '../type';
import { defaultOptions, getProgressChartOptions } from '../chartWidgetUtility';
import LoaderComponent from 'components/Loader';
import HighlightedText from "components/GlobalSearch/HighlightText";
import { InfoTooltip } from '../InfoTooltip';
import CustomSelectDropdown from 'components/Dropdown/CustomSelectDropdown';

const GRID_LEFT_CONFIG = {
  cost: [
    { maxWidth: 420, value: '21%' },
    { maxWidth: 600, value: '18%' },
    { maxWidth: 820, value: '15%' },
    { maxWidth: 920, value: '12%' },
    { maxWidth: 1260, value: '10%' },
    { maxWidth: Infinity, value: '8%' }
  ],
  nonCost: [
    { maxWidth: 420, value: '18%' },
    { maxWidth: 600, value: '15%' },
    { maxWidth: 820, value: '12%' },
    { maxWidth: 920, value: '9%' },
    { maxWidth: 1260, value: '7%' },
    { maxWidth: Infinity, value: '5%' }
  ]
};

const calculateGridLeft = (width: number, isCostChart: boolean, title: string): string => {
  if (title === 'Total Cost Spends') {
    return '2%';
  }
  const config = isCostChart ? GRID_LEFT_CONFIG.cost : GRID_LEFT_CONFIG.nonCost;
  return config.find(item => width < item.maxWidth)?.value || '5%';
};

const getThemeColors = (colors: any) => ({
  text: colors.widget.text.main,
  border: colors.widget.chart.legendTextColor,
  background: colors.widget.background.chart,
  scrollHandle: colors.widget.colors.color8,
  scrollFiller: colors.widget.chart.emptyCircleFill
});

const getBaseChartOption = (
  result: any, 
  gridLeft: string, 
  seriesData: any[], 
  defaultOptions: any
) => {
  if (!seriesData.length) {
    return defaultOptions;
  }
  
  return {
    ...result,
    grid: {
      ...result.grid,
      left: gridLeft
    }
  };
};

const getNewThemeOption = (adjustedOption: any, colors: any) => {
  if (!adjustedOption) return adjustedOption;

  return {
    ...adjustedOption,
    backgroundColor: colors.widget.background.chart,
    xAxis: getXAxisNewTheme(adjustedOption.xAxis, colors),
    yAxis: getYAxisNewTheme(adjustedOption.yAxis, colors),
    tooltip: getTooltipNewTheme(adjustedOption.tooltip, colors),
    legend: getLegendNewTheme(adjustedOption.legend, colors)
  };
};

const getXAxisNewTheme = (xAxis: any, colors: any) => {
  if (!xAxis) return undefined;
  
  return {
    ...xAxis,
    axisLabel: { ...xAxis.axisLabel, color: colors.widget.text.value },
    splitLine: getSplitLineNewTheme(xAxis.splitLine, colors)
  };
};

const getSplitLineNewTheme = (splitLine: any, colors: any) => {
  if (!splitLine) return undefined;
  
  return {
    ...splitLine,
    lineStyle: { ...splitLine.lineStyle, color: colors.widget.chart.emptyCircleFill }
  };
};

const getYAxisNewTheme = (yAxis: any, colors: any) => {
  if (!yAxis) return undefined;
  
  return {
    ...yAxis,
    axisLabel: { ...yAxis.axisLabel, color: colors.widget.text.value }
  };
};

const getTooltipNewTheme = (tooltip: any, colors: any) => {
  if (!tooltip) return undefined;
  
  return {
    ...tooltip,
    backgroundColor: colors.widget.chart.tooltipBg,
    textStyle: { ...tooltip.textStyle, color: colors.widget.chart.tooltipText },
    borderColor: colors.widget.chart.emptyCircleFill
  };
};

const getLegendNewTheme = (legend: any, colors: any) => {
  if (!legend) return undefined;
  
  return {
    ...legend,
    textStyle: { ...legend.textStyle, color: colors.widget.chart.legendTextColor }
  };
};

const getScrollOption = (
  adjustedOption: any, 
  maxVisibleBars: number, 
  dataLength: number, 
  colors: any
) => {
  const percentSpan = Math.min(100, (maxVisibleBars / dataLength) * 100);
  const startPercent = 100 - percentSpan;
  const endPercent = 100;
  const themeColors = getThemeColors(colors);
  
  return {
    ...adjustedOption,
    grid: {
      ...adjustedOption.grid,
      right: '16%',
    },
    dataZoom: [
      getSliderDataZoom(startPercent, endPercent, themeColors),
      getInsideDataZoom(startPercent, endPercent)
    ]
  };
};

const getSliderDataZoom = (startPercent: number, endPercent: number, colors: ReturnType<typeof getThemeColors>) => ({
  type: 'slider',
  yAxisIndex: 0,
  start: startPercent,
  end: endPercent,
  right: 8,
  width: 12,
  borderColor: 'transparent',
  fillerColor: colors.scrollFiller,
  handleStyle: { color: colors.scrollHandle },
  textStyle: { color: colors.text, fontSize: 10 },
  dataBackground: {
    lineStyle: { color: colors.border },
    areaStyle: { color: 'transparent' },
  },
  showDetail: false,
  zoomLock: true,
  brushSelect: false,
  moveOnMouseMove: true,
});

const getInsideDataZoom = (startPercent: number, endPercent: number) => ({
  type: 'inside',
  yAxisIndex: 0,
  start: startPercent,
  end: endPercent,
  zoomOnMouseWheel: true,
  moveOnMouseMove: true,
  moveOnMouseWheel: true,
});

const shouldShowScroll = (
  useScroll: boolean, 
  dataLength: number, 
  chartByCost: number | undefined
): boolean => {
  return useScroll && dataLength > 0 && (chartByCost === 0 || chartByCost === 1);
};

const WidgetTitle = ({ 
  title, 
  tooltipLabel,
  colors
}: any) => (
  <Stack direction="row" alignItems="center">
    <Typography
      variant="widgetTitleNew"
      sx={{ color: `${colors.widget.text.title} !important`, p: 2}}
    >
      {typeof title === 'string' ? <HighlightedText text={title} /> : title}
    </Typography>
    {!!tooltipLabel && <InfoTooltip label={tooltipLabel} />}
  </Stack>
);


const WidgetDropdown = ({ 
  dropdownValue, 
  dropdownOptions, 
  onDropdownChange, 
  colors
}: any) => (
  <CustomSelectDropdown
    value={dropdownValue}
    options={dropdownOptions}
    handleChange={(e) => onDropdownChange?.(e.target.value)}
    boldSelectedValue={true}
    dropdownIcon={KeyboardArrowDownIcon}
    boxProps={{
      sx: {
        backgroundColor: 'transparent !important',
        boxShadow: 'none',
        border: 'none',
      }
    }}
    selectComponentProps={{
      sx: {
        backgroundColor: 'transparent !important',
        boxShadow: 'none',

        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none !important',
          borderColor: 'transparent !important',
        },

        '&:hover .MuiOutlinedInput-notchedOutline': {
          border: 'none !important',
          borderColor: 'transparent !important',
        },

        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: 'none !important',
          borderColor: 'transparent !important',
        },

        '& .MuiSelect-select': {
          color: colors.widget.text.title,
          backgroundColor: 'transparent !important',
          fontWeight: 600,
          fontFamily: 'Montserrat',
          pr: '36px !important',
          pl: '0px !important',
        },

        '& .MuiSelect-icon': {
          color: colors.widget.text.title,
          right: '4px',
        }
      }
    }}
    menuProps={{
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left'
      },
      disablePortal: true,
    }}
  />
);

const ChartInfoTooltip = ({ tooltipText, colors }: any) => (
  <Tooltip
    title={
      <Box>
        <Typography variant="body2">
          {tooltipText}
        </Typography>
      </Box>
    }
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          zIndex: 9999,
          backgroundColor: colors.widget.chart.tooltipBg,
          color: colors.widget.chart.tooltipText,
          border: `1px solid ${colors.widget.chart.emptyCircleFill}`,
          '& .MuiTooltip-arrow': {
            zIndex: 9999,
            color: colors.widget.chart.tooltipBg
          }
        }
      }
    }}
  >
    <IconButton
      sx={{
        position: 'absolute',
        top: 12,
        right: 8,
        zIndex: 10,
      }}
    >
      <InfoIcon sx={{ color: colors.widget.text.title, fontSize: '16px' }} />
    </IconButton>
  </Tooltip>
);

// Main Component
const ProgressChartWidgetNew = ({
  isLoading = false,
  isOverlayWidget = false,
  title = '',
  tooltipLabel = null,
  data,
  envNames,
  seriesData,
  minTitleHeight = '50.5px',
  handleEvent,
  chartByCost,
  axisMax,
  containerProps,
  hasTooltip = false,
  tooltipText,
  hasDropdown = false,
  dropdownValue = '',
  dropdownOptions = [],
  onDropdownChange,
  maxVisibleBars,
  chartMinHeight = '430px',
  chartTop = '0px'
}: Readonly<ProgressChartWidgetProps>) => {
  const theme = useTheme();
  const colors = theme.palette;
  const dataLength = data?.length ?? 0;
  const useScroll = maxVisibleBars != null && dataLength > maxVisibleBars;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [gridLeft, setGridLeft] = useState('5%');

  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartContainerRef.current) {
        const containerWidth = chartContainerRef.current.offsetWidth;
        const isCostChart = chartByCost === 0 || chartByCost === 1;
        const calculatedLeft = calculateGridLeft(containerWidth, isCostChart, title);
        setGridLeft(calculatedLeft);
        setWindowSize(window.innerWidth);
      }
    }, 200);

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chartByCost, title]);

  const result = getProgressChartOptions(
    data ?? [], 
    envNames, 
    seriesData, 
    chartContainerRef?.current?.offsetWidth, 
    chartByCost, 
    axisMax
  );

  let adjustedOption = getBaseChartOption(result, gridLeft, seriesData, defaultOptions);

  adjustedOption = getNewThemeOption(adjustedOption, colors);

  if (shouldShowScroll(useScroll, dataLength, chartByCost) && maxVisibleBars) {
    adjustedOption = getScrollOption(adjustedOption, maxVisibleBars, dataLength, colors);
  }

  const handleChartClick = (params: ECElementEvent) => {
    if (handleEvent && data?.length) {
      const fullProgramName = data[data.length - 1 - params.dataIndex]?.programname;
      handleEvent(params, fullProgramName);
    }
  };

  const chartEvents = handleEvent ? { click: handleChartClick } : undefined;

  return (
    <Box display="flex" flexGrow={1} height="100%" position="relative">
      <Stack
        minHeight="100px"
        bgcolor={colors.widget.background.card}
        fontFamily="Montserrat"
        borderRadius="4px"
        flexGrow={1}
        {...containerProps}
      >
        <Stack 
          direction="row" 
          gap={2} 
          justifyContent="space-between" 
          alignItems="start" 
          minHeight={minTitleHeight}
        >
          <WidgetTitle 
            title={title}
            isOverlayWidget={isOverlayWidget}
            tooltipLabel={tooltipLabel}
            colors={colors}
          />
          
          {hasDropdown && dropdownOptions.length > 0 && (
            <WidgetDropdown
              dropdownValue={dropdownValue}
              dropdownOptions={dropdownOptions}
              onDropdownChange={onDropdownChange}
              colors={colors}
            />
          )}
        </Stack>
        
        <Stack 
          minHeight={chartMinHeight} 
          top={chartTop}
          position="relative" 
          flexGrow={1} 
          key={windowSize} 
          justifyContent="center" 
          ref={chartContainerRef}
        >
          {!isLoading ? (
            <ReactECharts
              style={{ width: '100%', height: '100%' }}
              option={adjustedOption}
              onEvents={chartEvents}
              opts={{ renderer: 'canvas', width: 'auto' }}
              notMerge={true}
              lazyUpdate={true}
            />
          ) : (
            <LoaderComponent />
          )}
        </Stack>
      </Stack>
      
      {hasTooltip && tooltipText && (
        <ChartInfoTooltip tooltipText={tooltipText} colors={colors} />
      )}
    </Box>
  );
};

export default ProgressChartWidgetNew;